import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, MapPin, ArrowRight, Users, ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from 'lucide-react';
import { getTours } from '../services/api';
import {
  parseTurkishDateRange,
  daysUntil,
} from '../utils/dateParser';

const fmtPrice = (p) => new Intl.NumberFormat('tr-TR').format(p || 0);
const DEFAULT_VISIBLE = 10;

const buildEventList = (tours, { includeSoldOut = false } = {}) => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const events = [];
  for (const t of tours || []) {
    if (!t.active) continue;
    for (const pd of t.pricingDates || []) {
      const parsed = parseTurkishDateRange(pd.date);
      if (!parsed?.startDate) continue;
      if (parsed.startDate < now) continue;
      const capacity = pd.capacity > 0 ? pd.capacity : (t.groupSize || 15);
      const remaining = Math.max(0, capacity - (pd.booked || 0));
      const soldOut = pd.available === false || remaining === 0;
      if (soldOut && !includeSoldOut) continue;
      events.push({
        tourId: t.id,
        slug: t.slug,
        title: t.title,
        nights: t.nights,
        location: t.location,
        levelName: t.levelName,
        groupSize: t.groupSize,
        price: pd.perPerson && pd.perPerson > 0 ? pd.perPerson : t.salePrice,
        currency: t.currency || '₺',
        rawDate: pd.date,
        startDate: parsed.startDate,
        endDate: parsed.endDate,
        capacity,
        remaining,
        soldOut,
      });
    }
  }
  events.sort((a, b) => a.startDate - b.startDate);
  return events;
};

const weekdayOf = (d) =>
  d ? new Intl.DateTimeFormat('tr-TR', { weekday: 'long' }).format(d) : '';
const monthOf = (d) => (d ? new Intl.DateTimeFormat('tr-TR', { month: 'long' }).format(d) : '');

/** "24 – 27 Ekim" · "29 Ekim – 1 Kasım" · "24 Ekim" */
const readableRange = (start, end) => {
  if (!end || end.getDate() === start.getDate()) return `${start.getDate()} ${monthOf(start)}`;
  if (end.getMonth() === start.getMonth()) return `${start.getDate()} – ${end.getDate()} ${monthOf(end)}`;
  return `${start.getDate()} ${monthOf(start)} – ${end.getDate()} ${monthOf(end)}`;
};

/* ─── Calendar-style card (no photo) ─── */
const EventCard = ({ ev }) => {
  const countdown = daysUntil(ev.startDate);
  const lowSeats = !ev.soldOut && ev.capacity > 0 && ev.remaining <= 3;
  const filled = ev.capacity > 0
    ? Math.min(100, Math.max(6, Math.round(((ev.capacity - ev.remaining) / ev.capacity) * 100)))
    : 0;

  const Wrapper = ev.soldOut ? 'div' : Link;
  const wrapperProps = ev.soldOut ? {} : { to: `/tur/${ev.slug}` };

  return (
    <Wrapper
      {...wrapperProps}
      data-testid={`upcoming-event-${ev.tourId}`}
      className={`group relative flex h-full flex-col rounded-2xl border bg-white overflow-hidden transition-[transform,box-shadow,border-color] duration-300 ${
        ev.soldOut
          ? 'border-gray-200 opacity-60 cursor-not-allowed'
          : 'border-gray-200 hover:border-[#3D4A26]/45 hover:shadow-[0_14px_34px_-18px_rgba(61,74,38,0.45)] hover:-translate-y-0.5'
      }`}
    >
      {/* Calendar head */}
      <div className={`relative px-4 pt-3.5 pb-5 ${ev.soldOut ? 'bg-gray-500' : 'bg-[#3D4A26]'}`}>
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 88% 12%, #C8D843 0, transparent 46%), radial-gradient(circle at 8% 92%, #C8D843 0, transparent 42%)',
          }}
        />
        <div className="relative flex items-start justify-between gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#C8D843]">
            {ev.startDate.getFullYear()}
          </span>
          {ev.soldOut ? (
            <span
              data-testid={`upcoming-soldout-${ev.tourId}`}
              className="text-[9px] font-bold uppercase tracking-wider bg-white/15 text-white px-2 py-0.5 rounded-full"
            >
              Tükendi
            </span>
          ) : ev.levelName ? (
            <span className="text-[9px] font-bold uppercase tracking-wider bg-white/15 text-white px-2 py-0.5 rounded-full">
              {ev.levelName}
            </span>
          ) : null}
        </div>

        <p
          className="relative text-[26px] sm:text-[28px] leading-tight font-bold text-white mt-1.5"
          style={{ fontFamily: 'DM Sans, sans-serif', letterSpacing: '-0.02em' }}
          data-testid={`upcoming-date-${ev.tourId}`}
        >
          {readableRange(ev.startDate, ev.endDate)}
        </p>

        <p className="relative text-xs font-medium text-white/75 mt-1.5">
          {weekdayOf(ev.startDate)}
          {ev.endDate && ev.endDate.getDate() !== ev.startDate.getDate() ? ` – ${weekdayOf(ev.endDate)}` : ''}
          {ev.nights ? ` · ${ev.nights} gece` : ''}
        </p>

        {/* Ticket seam */}
        <span aria-hidden="true" className="absolute -bottom-2.5 -left-2.5 w-5 h-5 rounded-full bg-[#FAFAF7]" />
        <span aria-hidden="true" className="absolute -bottom-2.5 -right-2.5 w-5 h-5 rounded-full bg-[#FAFAF7]" />
      </div>
      <span aria-hidden="true" className="block border-t border-dashed border-gray-200 mx-3" />

      {/* Body */}
      <div className="flex flex-1 flex-col p-4">
        <h3
          className={`text-[15px] font-bold leading-snug line-clamp-2 mb-2 transition-colors ${
            ev.soldOut ? 'text-gray-500' : 'text-[#1B1B1B] group-hover:text-[#3D4A26]'
          }`}
          style={{ fontFamily: 'DM Sans, sans-serif' }}
        >
          {ev.title}
        </h3>

        <div className="flex items-center gap-3 text-xs text-gray-500 mb-3 flex-wrap">
          <span className="inline-flex items-center gap-1"><MapPin size={11} /> {ev.location}</span>
          <span className="inline-flex items-center gap-1"><Users size={11} /> Max {ev.groupSize || 15}</span>
        </div>

        {!ev.soldOut && countdown !== null && countdown >= 0 && (
          <span
            className={`self-start inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full mb-3 ${
              countdown <= 7 ? 'bg-red-50 text-red-600' : 'bg-[#f0f4d8] text-[#3D4A26]'
            }`}
          >
            <CalendarDays size={11} />
            {countdown === 0 ? 'Bugün başlıyor' : countdown === 1 ? 'Yarın başlıyor' : `${countdown} gün kaldı`}
          </span>
        )}

        {!ev.soldOut && ev.capacity > 0 && (
          <div className="mb-3">
            <div className="flex items-center justify-between text-[11px] font-semibold mb-1.5">
              <span className={lowSeats ? 'text-red-500' : 'text-gray-500'}>
                {lowSeats ? `Son ${ev.remaining} yer` : 'Kontenjan'}
              </span>
              <span
                data-testid={`upcoming-seats-${ev.tourId}`}
                className={`tabular-nums ${lowSeats ? 'text-red-500' : 'text-gray-400'}`}
              >
                {ev.remaining}/{ev.capacity} kişi
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${lowSeats ? 'bg-red-500' : 'bg-[#C8D843]'}`}
                style={{ width: `${filled}%` }}
              />
            </div>
          </div>
        )}

        <div className="mt-auto flex items-end justify-between pt-3 border-t border-gray-100">
          <div>
            <p className="text-[9px] uppercase tracking-[0.18em] text-gray-400 font-bold">Kişi başı</p>
            <p className="text-lg font-bold text-[#1B1B1B] leading-tight" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              {ev.currency}{fmtPrice(ev.price)}
            </p>
          </div>
          {!ev.soldOut && (
            <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#3D4A26] bg-[#f0f4d8] px-3 py-2 rounded-full group-hover:bg-[#C8D843] group-hover:gap-2.5 transition-all">
              Detay <ArrowRight size={12} />
            </span>
          )}
        </div>
      </div>
    </Wrapper>
  );
};

/* ─── Horizontal snap carousel ─── */
const EventCarousel = ({ events }) => {
  const trackRef = React.useRef(null);
  const [active, setActive] = useState(0);
  const [edges, setEdges] = useState({ start: true, end: false });

  const measure = () => {
    const el = trackRef.current;
    if (!el) return;
    const step = el.firstChild ? el.firstChild.offsetWidth + 16 : 1;
    setActive(Math.round(el.scrollLeft / step));
    setEdges({
      start: el.scrollLeft <= 4,
      end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 4,
    });
  };

  useEffect(() => {
    measure();
  }, [events.length]);

  const nudge = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const step = el.firstChild ? el.firstChild.offsetWidth + 16 : 320;
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  return (
    <div className="relative">
      <div
        ref={trackRef}
        onScroll={measure}
        data-testid="upcoming-events-track"
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth -mx-4 px-4 sm:-mx-6 sm:px-6 lg:mx-0 lg:px-0 pb-1 [&::-webkit-scrollbar]:hidden"
        style={{ scrollbarWidth: 'none' }}
      >
        {events.map((ev, i) => (
          <div
            key={`${ev.tourId}-${i}-${ev.rawDate}`}
            className="snap-start shrink-0 w-[80vw] max-w-[300px] sm:w-[300px] lg:w-[320px]"
          >
            <EventCard ev={ev} />
          </div>
        ))}
        <span aria-hidden="true" className="shrink-0 w-1 sm:w-2" />
      </div>

      {/* Desktop arrows */}
      <button
        type="button"
        onClick={() => nudge(-1)}
        disabled={edges.start}
        aria-label="Önceki tarihler"
        data-testid="upcoming-events-prev"
        className="hidden lg:flex absolute -left-5 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border border-gray-200 shadow-md items-center justify-center text-[#1B1B1B] transition-opacity hover:border-[#3D4A26] disabled:opacity-0"
      >
        <ChevronLeft size={18} />
      </button>
      <button
        type="button"
        onClick={() => nudge(1)}
        disabled={edges.end}
        aria-label="Sonraki tarihler"
        data-testid="upcoming-events-next"
        className="hidden lg:flex absolute -right-5 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border border-gray-200 shadow-md items-center justify-center text-[#1B1B1B] transition-opacity hover:border-[#3D4A26] disabled:opacity-0"
      >
        <ChevronRight size={18} />
      </button>

      {/* Mobile dots */}
      {events.length > 1 && (
        <div className="lg:hidden flex items-center justify-center gap-1.5 mt-4" data-testid="upcoming-events-dots">
          {events.slice(0, 12).map((_, i) => (
            <span
              key={i}
              className={`rounded-full transition-all duration-300 ${
                i === active ? 'w-5 h-1.5 bg-[#3D4A26]' : 'w-1.5 h-1.5 bg-[#3D4A26]/25'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const UpcomingEvents = () => {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    getTours({ limit: 100 })
      .then((r) => setTours(r.data || []))
      .catch(() => setTours([]))
      .finally(() => setLoading(false));
  }, []);

  const activeEvents = useMemo(() => buildEventList(tours, { includeSoldOut: false }), [tours]);
  const allEvents = useMemo(() => buildEventList(tours, { includeSoldOut: true }), [tours]);
  const events = showAll ? allEvents : activeEvents.slice(0, DEFAULT_VISIBLE);
  const canExpand = allEvents.length > DEFAULT_VISIBLE || (allEvents.length > activeEvents.slice(0, DEFAULT_VISIBLE).length);

  if (loading || activeEvents.length === 0) return null;

  return (
    <section className="py-10 md:py-20 bg-[#FAFAF7]" data-testid="upcoming-events-section">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6 md:mb-10">
          <p className="text-[11px] md:text-xs uppercase tracking-[0.2em] font-semibold text-[#3D4A26] mb-1.5 md:mb-2">
            Yaklaşan Etkinlikler
          </p>
          <h2
            className="text-2xl md:text-4xl lg:text-5xl font-bold text-[#1B1B1B] leading-tight"
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            Sıradaki gruba katılın
          </h2>
          <p className="text-gray-600 mt-1.5 md:mt-2 text-sm md:text-base max-w-xl">
            Kokartlı rehberlerimizle {showAll ? 'tüm planlı tarihler' : 'en yakın 10 tarih'}. Yana kaydırarak gezinin, kontenjan sınırlı.
          </p>
        </div>

        {/* Mobile & desktop: horizontal snap carousel */}
        <EventCarousel events={events} />

        {/* Toggle: show all / collapse */}
        {canExpand && (
          <div className="mt-6 md:mt-8 flex justify-center">
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              data-testid="upcoming-events-toggle"
              aria-expanded={showAll}
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#1B1B1B] border-b-2 border-[#C8D843] pb-0.5 hover:text-[#3D4A26] hover:border-[#3D4A26] transition-colors"
            >
              {showAll ? (<>Daha az göster <ChevronUp size={14} /></>) : (<>Tüm tarihleri gör ({allEvents.length}) <ChevronDown size={14} /></>)}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default UpcomingEvents;
